// Notas de voz con el micrófono del navegador. Opus a 32 kbps: un minuto pesa ~240 KB.
// Chrome y Firefox graban WebM/Ogg; Safari, MP4 (AAC).

import { useCallback, useEffect, useRef, useState } from "react";
import { conDuracionWebm } from "./duracionWebm";

export const DURACION_MAXIMA_S = 5 * 60;
const FORMATOS = [
  { tipo: "audio/webm;codecs=opus", extension: "webm" },
  { tipo: "audio/ogg;codecs=opus", extension: "ogg" },
  { tipo: "audio/mp4", extension: "m4a" },
];

const formatoDisponible = () => (typeof MediaRecorder === "undefined"
  ? null
  : FORMATOS.find((formato) => MediaRecorder.isTypeSupported?.(formato.tipo)) || null);

const fechaParaNombre = (ahora = new Date()) => {
  const dos = (n) => String(n).padStart(2, "0");
  return `${ahora.getFullYear()}${dos(ahora.getMonth() + 1)}${dos(ahora.getDate())}-${dos(ahora.getHours())}${dos(ahora.getMinutes())}${dos(ahora.getSeconds())}`;
};

const apagar = (flujo) => flujo?.getTracks().forEach((pista) => pista.stop());

/**
 * @param {Function} [alLlegarAlMaximo] - recibe la nota cuando se cumple la duración máxima
 */
export default function useGrabadora({ alLlegarAlMaximo } = {}) {
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [error, setError] = useState("");
  const grabadoraRef = useRef(null);
  const flujoRef = useRef(null);
  const partesRef = useRef([]);
  const inicioRef = useRef(0);
  const relojRef = useRef(null);
  const alTerminarRef = useRef(null);
  // Mientras el navegador pide permiso para el micrófono.
  const pidiendoRef = useRef(false);
  // Cambia al empezar, al cancelar y al cerrarse la conversación: si el permiso llega
  // cuando la grabación ya no corresponde, el micrófono se apaga en vez de quedar grabando.
  const turnoRef = useRef(0);
  const alLlegarAlMaximoRef = useRef(alLlegarAlMaximo);
  alLlegarAlMaximoRef.current = alLlegarAlMaximo;

  const disponible = typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia) && Boolean(formatoDisponible());

  const soltarMicrofono = () => {
    clearInterval(relojRef.current);
    apagar(flujoRef.current);
    flujoRef.current = null;
    grabadoraRef.current = null;
    setGrabando(false);
  };

  /** @returns {Promise<{ archivo: File, duracion: number }|null>} */
  const terminar = useCallback(() => new Promise((resolve) => {
    const grabadora = grabadoraRef.current;
    // Sin grabación, o ya se está terminando (el máximo y el botón a la vez).
    if (!grabadora || grabadora.state === "inactive") {
      resolve(null);
      return;
    }
    const formato = FORMATOS.find((item) => grabadora.mimeType?.startsWith(item.tipo.split(";")[0])) || formatoDisponible();
    const duracionMs = Date.now() - inicioRef.current;
    alTerminarRef.current = async () => {
      const tipo = (grabadora.mimeType || formato.tipo).split(";")[0];
      let contenido = new Blob(partesRef.current, { type: tipo });
      partesRef.current = [];
      if (!contenido.size) {
        resolve(null);
        return;
      }
      if (tipo === "audio/webm") contenido = await conDuracionWebm(contenido, duracionMs);
      const archivo = new File([contenido], `nota-de-voz-${fechaParaNombre()}.${formato.extension}`, { type: tipo });
      resolve({ archivo, duracion: Math.max(1, Math.round(duracionMs / 1000)) });
    };
    grabadora.stop();
  }), []);

  const cancelar = useCallback(() => {
    turnoRef.current += 1;
    const grabadora = grabadoraRef.current;
    // Si ya se está terminando para enviarla, se deja terminar.
    if (grabadora && grabadora.state !== "inactive") {
      alTerminarRef.current = () => {
        partesRef.current = [];
      };
      grabadora.stop();
    }
    soltarMicrofono();
  }, []);

  const empezar = useCallback(async () => {
    // Un segundo clic mientras el navegador pide permiso no abre otro micrófono.
    if (pidiendoRef.current || grabadoraRef.current) return;
    setError("");
    const formato = formatoDisponible();
    if (!formato || !navigator.mediaDevices?.getUserMedia) {
      setError("Este navegador no puede grabar notas de voz.");
      return;
    }
    turnoRef.current += 1;
    const turno = turnoRef.current;
    pidiendoRef.current = true;
    let flujo;
    try {
      flujo = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (fallo) {
      if (turno === turnoRef.current) {
        setError(fallo?.name === "NotAllowedError"
          ? "Permite el uso del micrófono en el navegador para grabar notas de voz."
          : "No se encontró un micrófono.");
      }
      return;
    } finally {
      pidiendoRef.current = false;
    }
    // Se canceló o se cerró la conversación mientras el navegador pedía permiso.
    if (turno !== turnoRef.current) {
      apagar(flujo);
      return;
    }

    flujoRef.current = flujo;
    const grabadora = new MediaRecorder(flujo, { mimeType: formato.tipo, audioBitsPerSecond: 32000 });
    partesRef.current = [];
    grabadora.ondataavailable = (evento) => {
      if (evento.data?.size) partesRef.current.push(evento.data);
    };
    grabadora.onstop = () => {
      soltarMicrofono();
      alTerminarRef.current?.();
      alTerminarRef.current = null;
    };
    grabadoraRef.current = grabadora;
    inicioRef.current = Date.now();
    setSegundos(0);
    setGrabando(true);
    grabadora.start(250);
    relojRef.current = setInterval(() => {
      const transcurridos = Math.floor((Date.now() - inicioRef.current) / 1000);
      setSegundos(transcurridos);
      if (transcurridos >= DURACION_MAXIMA_S) {
        clearInterval(relojRef.current);
        terminar().then((nota) => nota && alLlegarAlMaximoRef.current?.(nota));
      }
    }, 250);
  }, [terminar]);

  // Si se cierra la conversación grabando (o pidiendo permiso), el micrófono no queda
  // encendido y lo grabado se descarta.
  useEffect(() => () => {
    turnoRef.current += 1;
    clearInterval(relojRef.current);
    const grabadora = grabadoraRef.current;
    if (grabadora && grabadora.state !== "inactive") grabadora.stop();
    apagar(flujoRef.current);
  }, []);

  return { disponible, grabando, segundos, error, empezar, terminar, cancelar };
}
